import React, { useEffect, useState } from 'react';
import { useOutletContext, useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiUsers, FiSearch, FiKey, FiEdit2, FiShoppingBag, FiArrowLeft, FiArrowRight, FiX } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';

const statuses = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered', 'cancelled'];

export default function Customers() {
  const { store, lang } = useOutletContext<{ store: any; lang: DashLang }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchTimer, setSearchTimer] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  // Customer detail view
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [customerOrders, setCustomerOrders] = useState<any[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit phone modal
  const [editingCustomer, setEditingCustomer] = useState<any>(null);
  const [editPhone, setEditPhone] = useState('');
  const [savingPhone, setSavingPhone] = useState(false);

  const [sendingReset, setSendingReset] = useState<number | null>(null);
  const [viewOrder, setViewOrder] = useState<any>(null);

  const t = (en: string, ar: string) => lang === 'ar' ? ar : en;

  // Check if URL has customer ID to show
  const customerId = searchParams.get('id');

  useEffect(() => {
    if (store?.id) loadCustomers();
  }, [store?.id, page]);

  useEffect(() => {
    if (store?.id && customerId) {
      loadCustomerDetail(Number(customerId));
    } else {
      setSelectedCustomer(null);
    }
  }, [store?.id, customerId]);

  const loadCustomers = async (q?: string) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      if (q || search) params.set('search', q ?? search);
      const res = await api.get(`/stores/${store.id}/customers?${params}`);
      setCustomers(res.data.data || []);
      setLastPage(res.data.last_page || 1);
    } finally { setLoading(false); }
  };

  const handleSearch = (val: string) => {
    setSearch(val);
    if (searchTimer) clearTimeout(searchTimer);
    setSearchTimer(setTimeout(() => { setPage(1); loadCustomers(val); }, 400));
  };

  const loadCustomerDetail = async (id: number) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/stores/${store.id}/customers/${id}`);
      setSelectedCustomer(res.data.customer);
      setCustomerOrders(res.data.orders || []);
    } catch {
      toast.error(t('Customer not found', 'العميل غير موجود'));
      setSearchParams({});
    } finally { setDetailLoading(false); }
  };

  const openCustomer = (c: any) => {
    setSearchParams({ id: String(c.id) });
  };

  const closeDetail = () => {
    setSearchParams({});
    setSelectedCustomer(null);
  };

  const openEditPhone = (c: any) => {
    setEditingCustomer(c);
    setEditPhone(c.phone || '');
  };

  const savePhone = async () => {
    if (!editingCustomer) return;
    setSavingPhone(true);
    try {
      await api.put(`/stores/${store.id}/customers/${editingCustomer.id}`, { phone: editPhone || null });
      toast.success(t('Phone updated', 'تم تحديث الهاتف'));
      setEditingCustomer(null);
      if (selectedCustomer?.id === editingCustomer.id) {
        setSelectedCustomer({ ...selectedCustomer, phone: editPhone });
      }
      loadCustomers();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setSavingPhone(false); }
  };

  const sendReset = async (c: any) => {
    if (!window.confirm(t(
      `Send password reset email to ${c.email}?`,
      `إرسال بريد إعادة تعيين كلمة المرور إلى ${c.email}؟`
    ))) return;
    setSendingReset(c.id);
    try {
      await api.post(`/stores/${store.id}/customers/${c.id}/reset-password`);
      toast.success(t('Password reset email sent', 'تم إرسال بريد إعادة تعيين كلمة المرور'));
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setSendingReset(null); }
  };

  const viewOrderInOrders = (orderNumber: string) => {
    navigate('/dashboard/orders');
  };

  if (!store) return null;

  // Customer detail view
  if (customerId && (selectedCustomer || detailLoading)) {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
          <button className="btn btn-sm btn-white" onClick={closeDetail}>
            {lang === 'ar' ? <FiArrowRight /> : <FiArrowLeft />} {t('Back', 'رجوع')}
          </button>
          <h1 className="page-title" style={{ margin: 0 }}>
            {detailLoading ? t('Loading...', 'جاري التحميل...') : selectedCustomer?.name}
          </h1>
        </div>

        {detailLoading ? (
          <div className="empty-state"><p>{dt('loading', lang)}...</p></div>
        ) : selectedCustomer && (
          <>
            {/* Customer info card */}
            <div className="card" style={{ padding: 20, marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: '50%', background: 'var(--primary)',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, fontWeight: 700, flexShrink: 0,
                  }}>
                    {selectedCustomer.name?.charAt(0)?.toUpperCase()}
                  </div>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 18 }}>{selectedCustomer.name}</h2>
                    <div style={{ fontSize: 13, color: 'var(--gray-400)', direction: 'ltr', textAlign: lang === 'ar' ? 'right' : 'left' }}>{selectedCustomer.email}</div>
                    <div style={{ fontSize: 13, color: 'var(--gray-400)', direction: 'ltr', textAlign: lang === 'ar' ? 'right' : 'left', marginTop: 2 }}>
                      {selectedCustomer.phone || t('No phone', 'لا يوجد هاتف')}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--gray-300)', marginTop: 4 }}>
                      {t('Joined', 'انضم')} {new Date(selectedCustomer.created_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US')}
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm btn-white" onClick={() => openEditPhone(selectedCustomer)}>
                    <FiEdit2 size={13} style={{ marginInlineEnd: 4 }} /> {t('Edit Phone', 'تعديل الهاتف')}
                  </button>
                  <button
                    className="btn btn-sm btn-white"
                    onClick={() => sendReset(selectedCustomer)}
                    disabled={sendingReset === selectedCustomer.id}
                  >
                    <FiKey size={13} style={{ marginInlineEnd: 4 }} />
                    {sendingReset === selectedCustomer.id ? '...' : t('Reset Password', 'إعادة تعيين كلمة المرور')}
                  </button>
                </div>
              </div>
            </div>

            {/* Orders */}
            <h3 style={{ marginBottom: 12 }}>
              <FiShoppingBag size={16} style={{ marginInlineEnd: 6, marginBottom: -2 }} />
              {t('Orders', 'الطلبات')} ({customerOrders.length})
            </h3>
            {customerOrders.length === 0 ? (
              <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)' }}>
                {t('No orders yet', 'لا توجد طلبات بعد')}
              </div>
            ) : (
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>{dt('order_number', lang)}</th>
                      <th>{dt('total', lang)}</th>
                      <th>{dt('payment_status', lang)}</th>
                      <th>{dt('status', lang)}</th>
                      <th>{dt('date', lang)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customerOrders.map((order: any) => (
                      <tr key={order.id} onClick={() => setViewOrder(order)} style={{ cursor: 'pointer' }}>
                        <td><strong>{order.order_number}</strong></td>
                        <td><strong>{store.currency_symbol} {Number(order.total).toFixed(3)}</strong></td>
                        <td>
                          <span className={`badge ${order.payment_status === 'paid' ? 'badge-delivered' : 'badge-pending'}`}>
                            {order.payment_status === 'paid' ? dt('paid', lang) : dt('unpaid', lang)}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge-${order.status === 'delivered' ? 'delivered' : order.status === 'cancelled' ? 'cancelled' : 'pending'}`}>
                            {dt(order.status, lang)}
                          </span>
                        </td>
                        <td>{new Date(order.created_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* Edit Phone Modal */}
        {editingCustomer && (
          <div className="modal-overlay" onClick={() => setEditingCustomer(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
              <h2>{t('Edit Phone Number', 'تعديل رقم الهاتف')}</h2>
              <p style={{ fontSize: 13, color: 'var(--gray-400)', margin: '-8px 0 16px' }}>{editingCustomer.name}</p>
              <div className="form-group">
                <label className="form-label">{dt('phone', lang)}</label>
                <input className="form-input" dir="ltr" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button className="btn btn-white" onClick={() => setEditingCustomer(null)}>{dt('cancel', lang)}</button>
                <button className="btn btn-primary" onClick={savePhone} disabled={savingPhone}>
                  {savingPhone ? '...' : dt('save_changes', lang)}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Order Detail Modal */}
        {viewOrder && (
          <div className="modal-overlay" onClick={() => setViewOrder(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 550 }}>
              <h2>{dt('order', lang)} {viewOrder.order_number}</h2>
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, fontSize: 13 }}>
                  <div><span style={{ color: 'var(--gray-400)' }}>{dt('type', lang)}:</span> <strong>{viewOrder.type === 'delivery' ? dt('delivery', lang) : 'Pickup'}</strong></div>
                  <div><span style={{ color: 'var(--gray-400)' }}>{dt('payment_status', lang)}:</span> <span className={`badge ${viewOrder.payment_status === 'paid' ? 'badge-delivered' : 'badge-pending'}`}>{viewOrder.payment_status === 'paid' ? dt('paid', lang) : dt('unpaid', lang)}</span></div>
                  <div><span style={{ color: 'var(--gray-400)' }}>{dt('status', lang)}:</span> <strong>{dt(viewOrder.status, lang)}</strong></div>
                </div>
                {viewOrder.delivery_address && <p style={{ fontSize: 13, marginTop: 8 }}><strong>{dt('address', lang)}:</strong> {viewOrder.delivery_address}</p>}
                {viewOrder.notes && <p style={{ fontSize: 13 }}><strong>{dt('notes', lang)}:</strong> {viewOrder.notes}</p>}
                {viewOrder.payment_gateway && <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>{t('Gateway', 'البوابة')}: {viewOrder.payment_gateway}</p>}
                {viewOrder.transaction_id && <p style={{ fontSize: 12, color: 'var(--gray-400)' }}>{t('Transaction', 'المعاملة')}: {viewOrder.transaction_id}</p>}
              </div>
              {viewOrder.items && viewOrder.items.length > 0 && (
                <div className="data-table" style={{ marginBottom: 16 }}>
                  <table>
                    <thead>
                      <tr><th>{dt('item', lang)}</th><th>{dt('qty', lang)}</th><th>{dt('price', lang)}</th><th>{dt('total', lang)}</th></tr>
                    </thead>
                    <tbody>
                      {viewOrder.items.map((item: any) => (
                        <tr key={item.id}>
                          <td>{item.product_name}</td>
                          <td>{item.quantity}</td>
                          <td>{store.currency_symbol} {Number(item.price).toFixed(3)}</td>
                          <td>{store.currency_symbol} {Number(item.total).toFixed(3)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ textAlign: lang === 'ar' ? 'left' : 'right', fontSize: 14 }}>
                <p>{dt('subtotal', lang)}: {store.currency_symbol} {Number(viewOrder.subtotal).toFixed(3)}</p>
                {Number(viewOrder.delivery_fee) > 0 && <p>{dt('delivery', lang)}: {store.currency_symbol} {Number(viewOrder.delivery_fee).toFixed(3)}</p>}
                {Number(viewOrder.discount) > 0 && <p style={{ color: '#16a34a' }}>{dt('discount', lang)}: -{store.currency_symbol} {Number(viewOrder.discount).toFixed(3)}</p>}
                <p style={{ fontSize: 18, fontWeight: 700 }}>{dt('total', lang)}: {store.currency_symbol} {Number(viewOrder.total).toFixed(3)}</p>
              </div>
              <div className="modal-actions">
                <button className="btn btn-white" onClick={() => setViewOrder(null)}>{dt('close', lang)}</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Customer list view
  return (
    <div>
      <div className="page-header">
        <h1>{t('Customers', 'العملاء')}</h1>
      </div>

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <FiSearch style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', insetInlineStart: 12, color: 'var(--gray-400)' }} />
        <input
          className="form-input"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t('Search by name, email, or phone...', 'بحث بالاسم أو البريد أو الهاتف...')}
          style={{ paddingInlineStart: 36 }}
        />
      </div>

      {loading ? (
        <div className="empty-state"><p>{dt('loading', lang)}...</p></div>
      ) : customers.length === 0 ? (
        <div className="empty-state">
          <FiUsers />
          <h3>{t('No customers', 'لا يوجد عملاء')}</h3>
          <p>{t('Customers will appear here when they register', 'سيظهر العملاء هنا عند تسجيلهم')}</p>
        </div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{dt('name', lang)}</th>
                  <th>{dt('email', lang)}</th>
                  <th>{dt('phone', lang)}</th>
                  <th>{dt('orders', lang)}</th>
                  <th>{dt('date', lang)}</th>
                  <th>{dt('actions', lang)}</th>
                </tr>
              </thead>
              <tbody>
                {customers.map((c: any) => (
                  <tr key={c.id}>
                    <td>
                      <button
                        onClick={() => openCustomer(c)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, color: 'var(--primary)', fontFamily: 'inherit', fontSize: 'inherit', padding: 0 }}
                      >
                        {c.name}
                      </button>
                    </td>
                    <td style={{ direction: 'ltr', textAlign: lang === 'ar' ? 'right' : 'left' }}>{c.email}</td>
                    <td style={{ direction: 'ltr', textAlign: lang === 'ar' ? 'right' : 'left' }}>{c.phone || '—'}</td>
                    <td>{c.orders_count || 0}</td>
                    <td>{new Date(c.created_at).toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US')}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-sm btn-white" onClick={() => openCustomer(c)} title={dt('view', lang)}>
                          <FiShoppingBag size={13} />
                        </button>
                        <button className="btn btn-sm btn-white" onClick={() => openEditPhone(c)} title={t('Edit Phone', 'تعديل الهاتف')}>
                          <FiEdit2 size={13} />
                        </button>
                        <button
                          className="btn btn-sm btn-white"
                          onClick={() => sendReset(c)}
                          disabled={sendingReset === c.id}
                          title={t('Reset Password', 'إعادة تعيين كلمة المرور')}
                        >
                          <FiKey size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {lastPage > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
              <button className="btn btn-sm btn-white" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                {lang === 'ar' ? <FiArrowRight /> : <FiArrowLeft />}
              </button>
              <span style={{ padding: '6px 12px', fontSize: 13 }}>{page} / {lastPage}</span>
              <button className="btn btn-sm btn-white" onClick={() => setPage(p => Math.min(lastPage, p + 1))} disabled={page >= lastPage}>
                {lang === 'ar' ? <FiArrowLeft /> : <FiArrowRight />}
              </button>
            </div>
          )}
        </>
      )}

      {/* Edit Phone Modal */}
      {editingCustomer && (
        <div className="modal-overlay" onClick={() => setEditingCustomer(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <h2>{t('Edit Phone Number', 'تعديل رقم الهاتف')}</h2>
            <p style={{ fontSize: 13, color: 'var(--gray-400)', margin: '-8px 0 16px' }}>{editingCustomer.name}</p>
            <div className="form-group">
              <label className="form-label">{dt('phone', lang)}</label>
              <input className="form-input" dir="ltr" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button className="btn btn-white" onClick={() => setEditingCustomer(null)}>{dt('cancel', lang)}</button>
              <button className="btn btn-primary" onClick={savePhone} disabled={savingPhone}>
                {savingPhone ? '...' : dt('save_changes', lang)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
